import React, { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";
import {
  ActionButton,
  IDropdownOption,
  Dropdown as OfficeDropdown,
  Spinner,
} from "@fluentui/react";
import { PieChart, domUtils } from "cayo.ui";
import styled from "styled-components";
import { LongCounterValue, ProtectedItemsCounter } from "@cayo/shared/api/types/cayo-graph";
import { commonMessages } from "@cayo/shared/messages/common";
import useService from "@cayo/shared/services/hooks/useService";
import { INotificationMessage } from "@cayo/shared/services/notifications";
import { IComponentViewStorage } from "@cayo/shared/settings/viewStorage";
import { FCC } from "@cayo/shared/types/react";
import { AbsoluteEmptyContainer, FullHeightColumn } from "./common";
import WidgetContainer from "./Container";
import useWidgetSignals from "./dataset-signals.hook";
import WidgetHeader from "./Header";
import { IProtectedObjects } from "../../api/schema.api";
import ajax from "@cayo/shared/libs/ajax";
import injectViewStorage from "../Hoc/inject-view-storage";
import Row from "../Layout/Row";

interface IProtectedObjectSettings {
  selectedSystem?: string;
}

const ProtectedObjects: FCC<
  IProtectedObjects & {
    viewStorage: IComponentViewStorage<IProtectedObjectSettings>;
  }
> = (props) => {
  const intl = useIntl();

  const allSystems = intl.formatMessage(messages.allSystems);

  const { viewStorage, title } = props;
  const [updateTime, setUpdateTime] = useState(new Date());
  const { data, isLoading, reloadData } = ajax.useGet(props.url, {
    ...props,
    updateTime,
  });
  const [systemOptions, setSystemOptions] = useState<IDropdownOption[] | undefined>(undefined);
  const notificationService = useService("notificationService");

  useWidgetSignals({
    signal: "refresh",
    handler: reloadData,
  });

  useEffect(() => {
    const counter = data as ProtectedItemsCounter;
    let systems = counter?.objectsPerSystem?.map<IDropdownOption>((o) => ({
      key: o.systemName!,
      text: o.systemName!,
      title: o.systemName!,
    }));

    if (systems) {
      systems = [
        {
          key: allSystems,
          text: allSystems,
          title: allSystems,
        },
        ...systems,
      ];

      const settings = viewStorage.get();
      const selectedSystem = systems?.find((s) => s.key === settings?.selectedSystem);
      if (selectedSystem) {
        selectedSystem.selected = true;
      } else {
        systems[0].selected = true;
      }
    }

    setSystemOptions(systems);
  }, [data]);

  const handleChange = useCallback(
    (e: FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
      if (!option || !systemOptions) {
        return;
      }

      systemOptions.forEach((o) => (o.selected = undefined));
      option.selected = true;

      setSystemOptions([...systemOptions]);

      viewStorage.set({ selectedSystem: option!.key as string });
    },
    [systemOptions]
  );

  useEffect(() => {
    const onMessages = (msgs: INotificationMessage[]) => {
      if (msgs.length) {
        setUpdateTime(new Date());
      }
    };

    return notificationService.subscribe("statisticsCounter", onMessages);
  }, [updateTime]);

  const selectedSystem = systemOptions?.find((o) => o.selected)?.key || allSystems;

  const chartData = useMemo<LongCounterValue[] | undefined>(() => {
    const counter = data as ProtectedItemsCounter;
    if (!counter) {
      return undefined;
    }
    if (selectedSystem === allSystems) {
      return counter.totals?.length ? counter.totals : undefined;
    } else {
      return (
        counter.objectsPerSystem &&
        counter.objectsPerSystem?.find((s) => s.systemName === selectedSystem)?.objects
      );
    }
  }, [systemOptions, data]);

  return (
    <WidgetContainer stick={props.stick}>
      <WidgetHeader title={title!} updateTime={updateTime} onRefresh={reloadData} />

      {!chartData && (
        <FullHeightColumn>
          <AbsoluteEmptyContainerWithMargin>
            {isLoading ? (
              <Spinner label={intl.formatMessage(commonMessages.loading)} labelPosition="right" />
            ) : (
              <FormattedMessage
                id="counters-chart.no-statistics"
                defaultMessage="To see a diagram with protected objects add your domains or tenants first."
              />
            )}
          </AbsoluteEmptyContainerWithMargin>
        </FullHeightColumn>
      )}
      {chartData && <PieChart {...props.chart} data={chartData} />}

      <DropdownWrapper valign justifyContent="start" onMouseDown={domUtils.stopMouseDown}>
        <OfficeDropdown
          onChange={handleChange}
          options={systemOptions || []}
          selectedKey={selectedSystem}
          disabled={!chartData}
          style={{ visibility: chartData ? "visible" : "hidden" }}
          styles={{
            dropdown: { minWidth: "240px", whiteSpace: "nowrap" },
            dropdownItem: { whiteSpace: "nowrap" },
            label: { paddingRight: 4 },

            root: {
              minHeight: 28,
              alignSelf: "flex-end",
              display: systemOptions && systemOptions.length ? "flex" : "none",

              right: "20px",
              top: 0,
              marginRight: 4,
            },
          }}
        />

        <ActionButton
          iconProps={{ iconName: "Refresh" }}
          title={"Refresh"}
          styles={{ root: { height: 28, fontSize: 13, fontWeight: 400, visibility: "hidden" } }}
          onClick={reloadData}
        >
          <FormattedMessage id="counters-chart.buttons.refresh" defaultMessage="Refresh" />
        </ActionButton>
      </DropdownWrapper>
    </WidgetContainer>
  );
};

const messages = defineMessages({
  allSystems: {
    defaultMessage: "All systems",
    id: "counters-chart.all-systems",
  },
});

const AbsoluteEmptyContainerWithMargin = styled(AbsoluteEmptyContainer)`
  margin: 0 24px;
`;

const DropdownWrapper = styled(Row)`
  margin-top: 8px;
  margin-bottom: 2px;
`;

export default injectViewStorage(ProtectedObjects);
